'use client'

import { useCallback, useEffect, useState } from 'react'
import { GoogleMap, Marker } from '@react-google-maps/api'
import { GoogleMapsProvider } from '@/components/GoogleMapsService'
import { getTeaserIconSrc } from './pharmacyChainIcon'
import { appPath } from '@/lib/getPublicAppUrl'

interface PharmacyResultsMapPoint {
    fulfillment_owner: string | null
    latitude: number
    longitude: number
}

interface PharmacyResultsMapProps {
    points: PharmacyResultsMapPoint[]
    height?: number
    /** Zip/search centroid from universal search; anchors the map instead of fitBounds. */
    searchCenter?: { latitude: number; longitude: number } | null
    /** The customer's own location driving the proximity search — included in the
     *  fitted bounds alongside the result points (see fitToPoints) so a sparse result
     *  set (e.g. the only nearby Walmart, states away) is shown relative to where the
     *  customer actually is, rather than the map simply centering on that one result. */
    origin?: { latitude: number; longitude: number } | null
}

/** Hides Google's own points of interest and transit stops so only pharmacy markers show. */
export const PHARMACY_MAP_STYLES: google.maps.MapTypeStyle[] = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]

// Same non-interactive philosophy as GoogleLocationPreview (disableDefaultUI,
// gestureHandling: 'none') — this map is a static snapshot, not a widget the customer
// pans/zooms/clicks through ("No click-through to start" in the design note). Universal
// search passes searchCenter for a zip-focused fixed zoom; ComparePharmaciesDrawer keeps
// dynamic fitBounds when no search center is supplied.
const STATIC_MAP_OPTIONS: google.maps.MapOptions = {
    disableDefaultUI: true,
    keyboardShortcuts: false,
    clickableIcons: false,
    gestureHandling: 'none',
    styles: PHARMACY_MAP_STYLES,
}

const MARKER_SIZE = 32
const GENERIC_MARKER_ICON_SRC = appPath('/assets/pharmacy/generic-pharmacy-badge.svg')

// Marker icons must be self-contained image files — Google Maps rasterizes them onto
// an internal canvas, and an SVG that references a separate external image inside it
// (e.g. a wordmark crop composited onto a background circle at runtime) renders blank,
// so each chain's own complete circular badge (the Browse pharmacies teaser's asset)
// is used directly here instead of the list row's wordmark-on-neutral-circle treatment.
/** Self-contained circular badge for the chain (a pharmacy name or fulfillment owner), or the generic badge. */
export function getPharmacyMarkerIconSrc(fulfillmentOwner: string | null): string {
    return getTeaserIconSrc(fulfillmentOwner ?? '') ?? GENERIC_MARKER_ICON_SRC
}

function MapMarkers({ points }: { points: PharmacyResultsMapPoint[] }) {
    return (
        <>
            {points.map((point, index) => {
                const icon = {
                    url: getPharmacyMarkerIconSrc(point.fulfillment_owner),
                    scaledSize: new google.maps.Size(MARKER_SIZE, MARKER_SIZE),
                    anchor: new google.maps.Point(MARKER_SIZE / 2, MARKER_SIZE / 2),
                }
                return <Marker key={index} position={{ lat: point.latitude, lng: point.longitude }} icon={icon} />
            })}
        </>
    )
}

export function PharmacyResultsMap({ points, height = 177, searchCenter, origin }: PharmacyResultsMapProps) {
    const [map, setMap] = useState<google.maps.Map | null>(null)

    const fitToPoints = useCallback(
        (mapInstance: google.maps.Map) => {
            if (!points.length) return

            if (searchCenter) {
                mapInstance.setCenter({ lat: searchCenter.latitude, lng: searchCenter.longitude })
                mapInstance.setZoom(14)
                return
            }

            const bounds = new google.maps.LatLngBounds()
            // Anchors the bounds on the customer's own location too, so a sparse result
            // set — e.g. the only nearby Walmart, potentially states away — is fitted
            // relative to where the customer actually is, rather than the map zooming in
            // on the result alone and reading as "you are here."
            if (origin) bounds.extend({ lat: origin.latitude, lng: origin.longitude })
            points.forEach((point) => bounds.extend({ lat: point.latitude, lng: point.longitude }))

            // fitBounds on a zero-size bounds (a single point, with no origin to anchor
            // against) collapses to Google's maximum zoom, with no way back out since
            // this map is non-interactive — center on it at a fixed,
            // GoogleLocationPreview-matching zoom instead of fitting a zero-size bounds.
            if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                const [{ latitude, longitude }] = points
                mapInstance.setCenter({ lat: latitude, lng: longitude })
                mapInstance.setZoom(15)
                return
            }

            mapInstance.fitBounds(bounds, 24)
        },
        [points, searchCenter, origin],
    )

    useEffect(() => {
        if (map) fitToPoints(map)
    }, [map, fitToPoints])

    if (!points.length) return null

    return (
        <GoogleMapsProvider>
            <GoogleMap
                onLoad={setMap}
                options={STATIC_MAP_OPTIONS}
                // flexShrink: 0 keeps this from collapsing to 0 height — the map sits in a
                // fixed-height flex column (ComparePharmaciesDrawer's resultsContainer)
                // alongside a result list that can outgrow that height, and this element's
                // own overflow: hidden (added internally by @react-google-maps/api) would
                // otherwise make it the one flexbox shrinks to fit, rather than the list
                // simply scrolling as intended.
                mapContainerStyle={{ width: '100%', height: `${height}px`, borderRadius: '8px', flexShrink: 0 }}
            >
                <MapMarkers points={points} />
            </GoogleMap>
        </GoogleMapsProvider>
    )
}
